import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import api from '../config/api';

const PartnerPasswordSetup = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const userId = searchParams.get('userId'); // Partner user ID if team member invitation
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Determine if this is a partner user (team member) or main partner setup
  const isPartnerUser = !!userId;

  useEffect(() => {
    if (!token) {
      setError('Invalid or missing setup token. Please check your email link.');
    }
  }, [token]);

  const validatePassword = () => {
    if (password.length < 8) {
      setError('Password must be at least 8 characters long');
      return false;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validatePassword()) {
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Use different endpoint based on whether this is a team member or main partner
      const endpoint = isPartnerUser ? '/partner-users/setup-password' : '/partners/setup-password';
      const response = await api.post(endpoint, {
        token,
        password,
        ...(isPartnerUser && { userId }) // Include userId for partner users
      });

      // Store token and user/partner data
      localStorage.setItem('partnerToken', response.data.token);
      
      localStorage.setItem(
        'partner',
        JSON.stringify(response.data.partner || response.data.partnerUser)
      );
      
      // Navigate to partner dashboard
      navigate('/partner/dashboard');
    } catch (err) {
      console.error('Error setting up password:', err);
      setError(err.response?.data?.error || 'Failed to set up password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ py: 8 }}>
      <Box sx={{ maxWidth: 440, mx: 'auto' }}>
      <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography variant="h3" gutterBottom sx={{ fontWeight: 600 }}>
            Set Up Your Password
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome to Déjà Vu Nomad! Please create a secure password for your partner account.
          </Typography>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <TextField
            fullWidth
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              setError('');
            }}
            required
            disabled={loading || !token}
            sx={{ mb: 3 }}
            helperText="Must be at least 8 characters long"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                    disabled={loading || !token}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <TextField
            fullWidth
            label="Confirm Password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => {
              setConfirmPassword(e.target.value);
              setError('');
            }}
            required
            disabled={loading || !token}
            sx={{ mb: 4 }}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                    disabled={loading || !token}
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              )
            }}
          />

          <Button
            type="submit"
            variant="contained"
            fullWidth
            disabled={loading || !token}
            sx={{ py: 1.5 }}
          >
            {loading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} color="inherit" />
                Setting Up...
              </>
            ) : (
              'Set Password & Continue'
            )}
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ mt: 3, textAlign: 'center' }}>
            This link will expire in 24 hours. If it has expired, please contact support at{' '}
            <a href="mailto:info@dejavunomad.com" style={{ color: '#667eea' }}>
              info@dejavunomad.com
            </a>
          </Typography>
        </form>
      </Paper>
      </Box>
    </Container>
  );
};

export default PartnerPasswordSetup;

